import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect, useState } from "react";
import { NEW_CUSTOMER_BILLING_COMPACT_LINE } from "../../constants/newCustomerBilling";

/**
 * Shared "first-time customer billing" notice.
 *
 * Dismiss (X) is stored per logged-in user (Auth0 sub/email), not per browser
 * globally — so a new account on the same machine still sees the banner.
 * Dismiss on one screen hides it for that user on all screens.
 * Complete Profile uses dismissible={false} so signup always shows it once.
 */

export const BILLING_NOTICE_DISMISSED_KEY =
  "wowzer.billingWelcomeNotice.dismissed";

/** @deprecated Alias — use getBillingNoticeDismissKey(userKey) */
export const BILLING_NOTICE_ORGS_EMPTY_KEY = BILLING_NOTICE_DISMISSED_KEY;

const LEGACY_GLOBAL_DISMISS_KEYS = [
  BILLING_NOTICE_DISMISSED_KEY,
  "wowzer.billingWelcomeNotice.organizationsEmpty",
  "wowzer.billingWelcomeNotice.settingsProfile",
  "wowzer.billingWelcomeNotice.addOrgModal",
  "wowzer.billingWelcomeNotice.paymentMethods.dismissed",
];

export const getBillingNoticeDismissKey = (userKey) => {
  const id = String(userKey || "").trim();
  if (!id) {
    return BILLING_NOTICE_DISMISSED_KEY;
  }
  return `wowzer.billingWelcomeNotice.dismissed.${id}`;
};

const readStorageFlag = (key) => {
  if (typeof window === "undefined" || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(key) === "1";
  } catch (_) {
    return false;
  }
};

const writeStorageFlag = (key) => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(key, "1");
  } catch (_) {
    /* no-op */
  }
};

/** @param {string} [userKey] Auth0 `sub` or email */
export const isBillingNoticeDismissed = (userKey) => {
  const perUserKey = getBillingNoticeDismissKey(userKey);
  if (userKey && readStorageFlag(perUserKey)) {
    return true;
  }
  if (!userKey) {
    return LEGACY_GLOBAL_DISMISS_KEYS.some((key) => readStorageFlag(key));
  }
  return false;
};

/** @param {string} [userKey] Auth0 `sub` or email */
export const dismissBillingNoticeGlobally = (userKey) => {
  const key = getBillingNoticeDismissKey(userKey);
  writeStorageFlag(key);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("wowzer:billing-notice-dismissed", {
        detail: { dismissKey: key, userKey },
      })
    );
  }
};

const BillingWelcomeNotice = ({
  variant = "full",
  className = "",
  dismissible = true,
  onDismiss,
}) => {
  const { user } = useAuth0();
  const userKey = user?.sub || user?.email || "";

  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!dismissible) {
      setDismissed(false);
      return;
    }
    setDismissed(isBillingNoticeDismissed(userKey));
  }, [dismissible, userKey]);

  useEffect(() => {
    if (!dismissible || !userKey || typeof window === "undefined") {
      return undefined;
    }

    const perUserKey = getBillingNoticeDismissKey(userKey);

    const syncDismissed = () => {
      if (isBillingNoticeDismissed(userKey)) {
        setDismissed(true);
      }
    };

    const onStorage = (e) => {
      if (e.newValue === "1" && e.key === perUserKey) {
        syncDismissed();
      }
    };

    window.addEventListener("wowzer:billing-notice-dismissed", syncDismissed);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(
        "wowzer:billing-notice-dismissed",
        syncDismissed
      );
      window.removeEventListener("storage", onStorage);
    };
  }, [dismissible, userKey]);

  if (dismissed) return null;

  const handleDismiss = () => {
    dismissBillingNoticeGlobally(userKey);
    setDismissed(true);
    if (typeof onDismiss === "function") {
      onDismiss();
    }
  };

  const CloseButton = dismissible ? (
    <button
      type="button"
      aria-label="Dismiss billing notice"
      onClick={handleDismiss}
      className="absolute top-2 right-2 z-10 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-blue-700/70 hover:text-blue-900 hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="h-3.5 w-3.5"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    </button>
  ) : null;

  if (variant === "compact") {
    return (
      <div
        role="note"
        className={`relative w-full max-w-full box-border rounded-md border border-blue-200 bg-blue-50 px-4 py-3 ${
          dismissible ? "pr-10" : ""
        } text-sm text-blue-900 ${className}`}
      >
        <p className="m-0 mb-2 text-base font-bold uppercase tracking-wide text-blue-900">
          First 30 days free
        </p>
        <p className="m-0 text-sm leading-relaxed break-words text-blue-900">
          {NEW_CUSTOMER_BILLING_COMPACT_LINE}
        </p>
        {CloseButton}
      </div>
    );
  }

  return (
    <div
      role="note"
      className={`relative rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 ${
        dismissible ? "pr-10" : ""
      } text-blue-900 ${className}`}
    >
      <span className="block text-lg font-bold uppercase tracking-wide mb-1">
        First 30 days free
      </span>
      <p className="text-sm">{NEW_CUSTOMER_BILLING_COMPACT_LINE}</p>
      {CloseButton}
    </div>
  );
};

export default BillingWelcomeNotice;
